import {get} from 'svelte/store';
import {BehaviorSubject, Subject, combineLatest} from 'rxjs';
import {skipUntil, switchMap, map} from 'rxjs/operators';
import {goto} from '@sapper/app';
import {mc} from '@/core';
import {SERVICES} from '@/const/services';
import {PATHS} from '@/const/paths';
import {premiumApi} from '@/api/premium';
import {configApi} from '@/api/config';
import {lang$, fixLink$} from '@/store/lang';
import {CommissionMode} from '@/modules/header/const';
import {browser$} from '@/streams/browser';
import {userInfo$} from '@/streams/user';
import {kolApi} from '@/api/kol';

export const isShowPremium$ = new BehaviorSubject(false);
export const promotions$ = new BehaviorSubject([]);
export const stickyPromotions$ = new BehaviorSubject({title: '', link: '', fullCornerIcon: ''});
export const importantAnnouncementHeight$ = new BehaviorSubject(0);
export const ipPromptHeight$ = new BehaviorSubject(0);

browser$.subscribe(() => {
  loadPromotions();
});

export const queryPremium$ = new Subject();
export const isPremium$ = new BehaviorSubject({premium: false});

queryPremium$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadPremium(query))
  )
  .subscribe((x) => isPremium$.next(x));

export const queryStage$ = new Subject();
export const premiumStage$ = new BehaviorSubject({premiumStage: 0});

queryStage$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadStage(query))
  )
  .subscribe((x) => premiumStage$.next(x));

async function loadPromotions() {
  const lang = get(lang$);
  const fixLink = get(fixLink$);
  const convertLang = lang === 'cn' ? 'zh' : lang;
  const query = {itemKey: 'promotions', lang: convertLang};
  const {data} = await configApi.getPromotions(query);
  if (data) {
    const promotions = data.rows.map((o) => {
      const {webLink, cornerIcon} = o;
      return {...o, link: webLink.startsWith('https:') ? webLink : fixLink(webLink), fullCornerIcon: SERVICES.STATIC_URL + '/' + cornerIcon, cornerIcon};
    });
    const stickyPromotions = promotions.find((o) => o.isHotSpot) || [];
    const normalPromotions = promotions.filter((o) => !o.isHotSpot);
    stickyPromotions$.next(stickyPromotions);
    promotions$.next(normalPromotions);
  }
}

async function loadPremium() {
  const {data, error} = await premiumApi.getPremium();
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {};
}

async function loadStage() {
  const {data, error} = await premiumApi.getStage();
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {premiumStage: 0};
}

combineLatest([premiumStage$, isPremium$])
  .pipe(map(([premiumStage, isPremium]) => isPremium.premium && premiumStage.premiumStage >= 3))
  .subscribe((o) => isShowPremium$.next(o));

export const gotoReferral$ = new Subject();

combineLatest([userInfo$, gotoReferral$]).subscribe(([info]) => {
  const fixLink = get(fixLink$);
  if (info.referralType === 5) {
    gotoDashboard$.next();
    return;
  }
  goto(fixLink(PATHS.ACCOUNT_INVITE_FRIENDS.pathname));
});

export const gotoDashboard$ = new Subject();

gotoDashboard$.subscribe(async () => {
  const {data} = await kolApi.getUserProfile();
  const fixLink = get(fixLink$);
  if (data) {
    const {commissionMode} = data;
    if (commissionMode === CommissionMode.KOL) {
      goto(fixLink(PATHS.KOL_HOME.pathname));
      return;
    }
    if (commissionMode === CommissionMode.Partner) {
      goto(fixLink(PATHS.PARTNER_HOME.pathname));
      return;
    }
    goto(fixLink(PATHS.HOME.pathname));
  }
});
